import { getApiGatewayUrl } from '@trello/api-gateway';
import { Analytics } from '@trello/atlassian-analytics';
import { clientVersion } from '@trello/config';
import {
  getApiError,
  getErrorTextFromFetchResponse,
  isFetchCancellationError,
} from '@trello/error-handling';
import { sendErrorEvent } from '@trello/error-reporting';

import {
  cacheAccessibleJiraProducts,
  cacheAccessibleProducts,
  getCachedAccessibleJiraProducts,
  getCachedAccessibleProducts,
} from './getCachedAccessibleProducts';

type IDPAFPermissionIdType =
  | 'add-products'
  | 'external-collaborator-write'
  | 'invite-users'
  | 'manage'
  | 'write';

interface Workspace {
  cloudId?: string;
  cloudUrl?: string;
  workspaceAri: string;
  workspacePermissionIds: IDPAFPermissionIdType[];
  workspaceUrl: string;
  workspaceDisplayName: string;
  workspaceAvatarUrl: string;
  orgId: string;
}

interface Product {
  productId: string;
  productDisplayName: string;
  workspaces: Workspace[];
}

const productIds = [
  'confluence.ondemand',
  'jira-software.ondemand',
  'jira-servicedesk.ondemand',
  'jira-core.ondemand',
  'opsgenie',
  'trello',
  'bitbucket',
  'statuspage',
  'compass',
  'jira-product-discovery',
  'beacon',
  'atlassian-analytics-free',
  'mercury',
  'unified-help',
  'rovo.ondemand',
];

export const accessibleJiraProductIds = [
  'jira-software.ondemand',
  'jira-servicedesk.ondemand',
  'jira-core.ondemand',
] as const;

export interface AccessibleProductsResponse {
  products: Product[];
}

export const getAccessibleProductsUrl = (): string =>
  getApiGatewayUrl('/v2/accessible-products');

export type FetchAccessibleProductsResult =
  | { type: 'ok'; data: AccessibleProductsResponse }
  | { type: 'unauthorized' };

/**
 * POST /v2/accessible-products for the given product ids.
 * On 401 returns `{ type: 'unauthorized' }` (do not treat as cacheable).
 * Throws on other non-OK responses.
 */
export const fetchAccessibleProducts = async (
  productIdsToFetch: string[],
): Promise<FetchAccessibleProductsResult> => {
  const url = getAccessibleProductsUrl();
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Trello-Client-Version': clientVersion,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ productIds: productIdsToFetch }),
  });

  if (response.ok) {
    Analytics.sendOperationalEvent({
      action: 'fetched',
      actionSubject: 'fetchAccessibleProducts',
      source: 'crossFlowEssentials',
    });
    const resolvedResponse = await response.json();
    return { type: 'ok', data: resolvedResponse.data };
  }

  if (response.status === 401) {
    return { type: 'unauthorized' };
  }

  const errorMessage = await getErrorTextFromFetchResponse(response);
  throw getApiError(response.status, errorMessage);
};

export const getAccessibleProducts =
  async (): Promise<AccessibleProductsResponse> => {
    try {
      const cachedAccessibleProducts = getCachedAccessibleProducts();
      if (cachedAccessibleProducts !== null) {
        return Promise.resolve({ products: cachedAccessibleProducts });
      }
      const result = await fetchAccessibleProducts(productIds);
      if (result.type === 'unauthorized') {
        return { products: [] };
      }
      cacheAccessibleProducts(result.data);
      return result.data;
    } catch (error) {
      if (error instanceof Error && !isFetchCancellationError(error)) {
        sendErrorEvent(error, {
          tags: {
            ownershipArea: 'trello-electric',
            feature: 'Cross Flow Provider',
          },
        });
      }

      return { products: [] };
    }
  };

export const getAccessibleJiraProducts =
  async (): Promise<AccessibleProductsResponse> => {
    try {
      const cachedAllProducts = getCachedAccessibleProducts();
      if (cachedAllProducts !== null) {
        return { products: cachedAllProducts };
      }
      const cachedJiraProducts = getCachedAccessibleJiraProducts();
      if (cachedJiraProducts !== null) {
        return { products: cachedJiraProducts };
      }
      const result = await fetchAccessibleProducts([
        ...accessibleJiraProductIds,
      ]);
      if (result.type === 'unauthorized') {
        return { products: [] };
      }
      cacheAccessibleJiraProducts(result.data);
      return result.data;
    } catch (error) {
      if (error instanceof Error && !isFetchCancellationError(error)) {
        sendErrorEvent(error, {
          tags: {
            ownershipArea: 'trello-electric',
            feature: 'Cross Flow Provider',
          },
        });
      }

      return { products: [] };
    }
  };
